/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  ApiError,
  createEmptyStateResource,
  EMPTY_STRING,
  hasStateResourceError,
  MessageCode,
  StateResource,
} from '@common/tech-shared';
import { SpinnerComponent } from '@ui';
import { CommonModule } from '@angular/common';
import { Component, effect, input } from '@angular/core';
import { IconComponent, TooltipDirective } from '@ods/system';
import { cva, VariantProps } from 'class-variance-authority';
import { isUndefined } from 'lodash-es';
import { userProfileMessage } from '../user-profile.message';
import { UserProfileResource } from '../user-profile.model';
import { getUserName, getUserNameInitials } from '../user-profile.util';

export const userIconVariants = cva(
  ['relative flex size-8 items-center justify-center overflow-hidden rounded-full text-lg text-white'],
  {
    variants: {
      variant: {
        profile: 'shrink-0',
        vorgang: 'rounded-full m-0 p-0.5 block',
      },
    },
  },
);

export type UserIconVariants = VariantProps<typeof userIconVariants>;

@Component({
  selector: 'alfa-user-icon',
  templateUrl: './user-icon.component.html',
  standalone: true,
  imports: [CommonModule, SpinnerComponent, IconComponent, TooltipDirective],
})
export class UserIconComponent {
  public readonly variant = input<UserIconVariants['variant']>();
  public readonly userProfileStateResource =
    input<StateResource<UserProfileResource>>(createEmptyStateResource<UserProfileResource>());
  public readonly hasTooltip = input<boolean>(true);

  readonly messageCode = MessageCode;

  public tooltip: string = EMPTY_STRING;

  constructor() {
    effect(() => {
      if (this.hasTooltip()) {
        this.tooltip = this._getTooltip();
      }
    });
  }

  _getTooltip(): string {
    if (this.userProfileStateResource().resource) {
      return this._getUserTooltip();
    }
    if (hasStateResourceError(this.userProfileStateResource())) {
      return this._getErrorTooltip();
    }
    return userProfileMessage.UNASSIGNED;
  }

  _getUserTooltip(): string {
    return getUserName(this.userProfileStateResource().resource);
  }

  _getErrorTooltip(): string {
    const message: string = userProfileMessage[this.errorMessageCode];
    return isUndefined(message) ? userProfileMessage.UNKNOW_ERROR : message;
  }

  public get errorMessageCode(): string {
    return (<ApiError>this.userProfileStateResource().error)?.issues[0]?.messageCode;
  }

  public get initials(): string {
    return getUserNameInitials(this.userProfileStateResource().resource);
  }

  protected readonly userIconVariants = userIconVariants;
}
